import { Observable } from 'rxjs';
import { BaseAsyncValue } from '@ordersnproducts/modules/shared/models/async-value';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import {
  getRmsRecipeSpecifications,
  getRmsSpecificationFormState,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { UnloadTypePumpCodes } from '@ordersnproducts/modules/shared/models/pump-type.enum';
import {
  getFirstCalendar,
  getRmsSpecificationPumpCapacity,
  getRmsSpecificationPumpLinearMeters,
  getRmsSpecificationUnloadType,
} from '@ordersnproducts/modules/order-builder/reducers';
import { PUMP_SIZES } from '@ordersnproducts/modules/shared/constants/pumping';
import { combineLatest } from 'rxjs';
import { PipeQuantityCodes } from '@ordersnproducts/modules/order-builder/modules/specification/services/commands/handle-pipe-quantity.command';

export namespace PumpRmsULProvider {
  /**
   * Service to validate and control the visibility of pump-related
   * fields based on the unload type and other conditions.
   *
   * This service checks the unload type and determines whether to
   * hide or show specific fields in the form.
   * It subscribes to the RMS recipe specifications and the first
   * calendar state to make these decisions.
   *
   * - If the unload type is either BOOM_PUMP or STATIONARY_PUMP and
   * the form is the first one (index 0), it further checks the calendar state.
   * - Depending on the calendar's pump flagLinearMeters value, it decides
   * the visibility of the field named LINEAR_METERS.
   * - If the unload type does not match the specified pump codes or
   * the form is not the first one, it hides the field.
   */
  export class ValidateDisplayPumpFields extends BaseAsyncValue<boolean> {
    getValue(): Observable<boolean> {
      const getRmsSpecificationForms$ = this.store.select(getRmsRecipeSpecifications);
      const getFirstCalendar$ = this.store.select(getFirstCalendar).pipe(
        filter((calendar) => !!calendar),
        map((calendar) => _safe(() => calendar.pump.flagLinearMeters, false)),
      );

      return getRmsSpecificationForms$.pipe(
        filter((_state) => _state.length > 0),
        withLatestFrom(getFirstCalendar$),
        map(([_state, hasLinearMeters]) => {
          const { id, unloadType } = this.form.getRawValue();
          const recipe = _state.find((x) => x.id === id);
          const index = _state.indexOf(recipe);

          let hide = true;

          if (unloadType) {
            if (index === 0) {
              if (
                [UnloadTypePumpCodes.BOOM_PUMP, UnloadTypePumpCodes.STATIONARY_PUMP].includes(
                  unloadType as UnloadTypePumpCodes,
                )
              ) {
                hide = this.config.name === SpecFieldNames.LINEAR_METERS ? !hasLinearMeters : hasLinearMeters;
              }
            }
          }

          return hide;
        }),
      );
    }
  }
  export class ValidatorPump extends BaseAsyncValue<boolean> {
    /**
     * Retrieves a value based on the RMS specification form state and unload type.
     *
     * @returns An Observable that emits a boolean indicating whether the unload type
     *          is not BOOM_PUMP or STATIONARY_PUMP.
     */
    getValue(): Observable<boolean> {
      return this.store.select(getRmsSpecificationFormState).pipe(
        withLatestFrom(this.store.select(getRmsSpecificationUnloadType)),
        map(([specifications, unloadType]) => {
          if (!unloadType) {
            return true;
          }
          const response = ![UnloadTypePumpCodes.BOOM_PUMP, UnloadTypePumpCodes.STATIONARY_PUMP].includes(
            unloadType as UnloadTypePumpCodes,
          );
          return response;
        }),
      );
    }
  }
  export class ValidatorPumpCapacityCO extends BaseAsyncValue<boolean> {
    /**
     * Retrieves a value based on the RMS specification unload type.
     *
     * @returns An Observable that emits a boolean indicating whether the unload type
     *          is not CEMEX_PUMP or CUSTOMER_PUMP.
     */
    getValue(): Observable<boolean> {
      return this.store.select(getRmsSpecificationUnloadType).pipe(
        map((unloadType) => {
          return ![UnloadTypePumpCodes.CEMEX_PUMP, UnloadTypePumpCodes.CUSTOMER_PUMP].includes(
            unloadType as UnloadTypePumpCodes,
          );
        }),
      );
    }
  }

  export class ValidatorPipeQuantityCO extends BaseAsyncValue<boolean> {
    /**
     * Retrieves a value based on the RMS specification quantity and unload type.
     *
     * @returns An Observable that emits a boolean indicating whether the unload type
     *          is not CEMEX_PUMP or CUSTOMER_PUMP.
     */
    getValue(): Observable<boolean> {
      return combineLatest([
        this.store.select(getRmsSpecificationUnloadType),
        this.store.select(getRmsSpecificationPumpCapacity),
      ]).pipe(
        map(([unloadType, capacity]) => {
          const isPump = [UnloadTypePumpCodes.CEMEX_PUMP, UnloadTypePumpCodes.CUSTOMER_PUMP].includes(
            unloadType as UnloadTypePumpCodes,
          );
          const isStationaryPump = capacity === PipeQuantityCodes.StationaryPipe;
          return !(isPump && isStationaryPump);
        }),
      );
    }
  }

  export class ValidatorPumpLength extends BaseAsyncValue<boolean> {
    /**
     * Retrieves an observable that emits a boolean value indicating whether the specific pump size code
     * does not match the selected pump capacity.
     *
     * @returns {Observable<boolean>} An observable that emits a boolean value.
     *
     * The observable is derived from the combination of the RMS specification form state and the selected pump capacity.
     * It completes when the `ngDestroy` observable emits a value.
     */
    getValue(): Observable<boolean> {
      return this.store.select(getRmsSpecificationFormState).pipe(
        withLatestFrom(this.store.select(getRmsSpecificationPumpCapacity)),
        map(([specifications, selectedPumpCapacity]) => {
          const result = PUMP_SIZES.specificSizeCode !== selectedPumpCapacity;
          return result;
        }),
        takeUntil(this.ngDestroy),
      );
    }
  }

  export class ValidatorPumpLengthDisable extends BaseAsyncValue<boolean> {
    /**
     * Retrieves an observable that emits a boolean value indicating whether the specific pump size code
     * does not match the selected pump capacity.
     *
     * @returns {Observable<boolean>} An observable that emits a boolean value.
     *
     * The observable is derived from the combination of the RMS specification form state and the selected pump capacity.
     * It completes when the `ngDestroy` observable emits a value.
     */
    getValue(): Observable<boolean> {
      return this.store.select(getRmsSpecificationFormState).pipe(
        withLatestFrom(this.store.select(getRmsSpecificationPumpCapacity)),
        map(([specifications, selectedPumpCapacity]) => {
          const result = PUMP_SIZES.specificSizeCode !== selectedPumpCapacity;
          return result === undefined ? false : result;
        }),
        takeUntil(this.ngDestroy),
      );
    }
  }

  export class ValidatorPumpLinearMeters extends BaseAsyncValue<boolean> {
    /**
     * @description
     * This method retrieves the value from the store and determines whether a certain UI element should be hidden or not.
     * It selects the RMS specification form state and the selected pump linear meters from the store.
     * If the selected pump linear meters matches certain pump codes, it further checks the calendar's pump flag for linear meters.
     * The result is an observable that emits a boolean indicating whether the UI element should be hidden.
     *
     * @returns {Observable<boolean>} An observable that emits a boolean indicating whether the UI element should be hidden.
     */
    getValue(): Observable<boolean> {
      return this.store.select(getRmsSpecificationFormState).pipe(
        withLatestFrom(this.store.select(getRmsSpecificationPumpLinearMeters)),
        withLatestFrom(
          this.store.select(getFirstCalendar).pipe(
            filter((calendar) => !!calendar),
            map((calendar) => _safe(() => calendar.pump.flagLinearMeters, false)),
          ),
        ),
        map(([[specifications, selectedPumpLinearMeters], hasLinearMeters]) => {
          let willHide = true;
          const unloadType = this.form.get(SpecFieldNames.UNLOAD_TYPE).value;
          if (
            [UnloadTypePumpCodes.PUMP, UnloadTypePumpCodes.BOOM_PUMP, UnloadTypePumpCodes.STATIONARY_PUMP].includes(
              unloadType as UnloadTypePumpCodes,
            )
          ) {
            willHide = !hasLinearMeters;
          }
          return willHide;
        }),
        takeUntil(this.ngDestroy),
      );
    }
  }
}
